import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { Paginated } from '@denwen-msp/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Popover } from './menu';
import { Spinner } from './spinner';

export interface Option {
  value: string;
  label: string;
  hint?: string;
}

interface ComboboxProps {
  value: string | null | undefined;
  /** `option` is the picked option (none when cleared), for pages that keep its label in a draft. */
  onChange: (value: string | null, option?: Option) => void;
  /** Static options... */
  options?: Option[];
  /** ...or load them from a list endpoint as the user types (`/organizations`). */
  endpoint?: string;
  endpointQuery?: Record<string, string>;
  toOption?: (row: never) => Option;
  /** Label for the current value when it isn't in the loaded page. */
  selectedLabel?: string | null;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-invalid'?: boolean;
  emptyText?: ReactNode;
}

/** Searchable picker for long or remote lists (organizations, contacts, users, products). */
export function Combobox(props: ComboboxProps) {
  const { value, onChange, options, endpoint, endpointQuery, toOption, selectedLabel, placeholder = 'Select…', clearable = true, disabled, id, className } = props;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const remote = useQuery({
    queryKey: ['combobox', endpoint, endpointQuery, q],
    queryFn: ({ signal }) => api<Paginated<never> | never[]>(endpoint!, { query: { ...endpointQuery, q, pageSize: 20 }, signal }),
    enabled: Boolean(endpoint) && open,
    staleTime: 30_000,
  });
  const rows = remote.data ? (Array.isArray(remote.data) ? remote.data : remote.data.items) : [];
  const list: Option[] = options ?? (toOption ? rows.map(toOption) : []);
  const filtered = options ? list.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : list;
  const current = list.find((o) => o.value === value)?.label ?? selectedLabel ?? null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ('');
      }}
      className="w-[var(--radix-popover-trigger-width)] min-w-64"
      trigger={
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-invalid={props['aria-invalid']}
          aria-haspopup="listbox"
          className={cn(
            'flex h-8 w-full items-center justify-between gap-2 rounded-md border border-border-strong bg-surface px-2.5 text-left text-sm shadow-card focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-50 aria-[invalid=true]:border-danger',
            className,
          )}
        >
          <span className={cn('truncate', !current && 'text-subtle')}>{current ?? placeholder}</span>
          <span className="flex items-center gap-1 text-subtle">
            {clearable && value ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear"
                className="rounded p-0.5 hover:bg-surface-hover hover:text-fg"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
              >
                <X className="size-3.5" />
              </span>
            ) : null}
            <ChevronsUpDown className="size-3.5" />
          </span>
        </button>
      }
    >
      <Command shouldFilter={false} className="text-sm">
        <div className="flex items-center border-b px-2">
          <Command.Input autoFocus value={q} onValueChange={setQ} placeholder="Search…" className="h-9 w-full bg-transparent outline-none placeholder:text-subtle" />
          {remote.isFetching ? <Spinner className="size-3.5 text-subtle" /> : null}
        </div>
        {/* Short of room (a dialog near the bottom of a small screen), the list shrinks and scrolls; the search box stays. */}
        <Command.List className="max-h-[min(16rem,calc(var(--radix-popover-content-available-height)-2.75rem))] overflow-y-auto overscroll-contain p-1">
          <Command.Empty className="px-2 py-3 text-center text-muted">{props.emptyText ?? 'No matches'}</Command.Empty>
          {filtered.map((o) => (
            <Command.Item
              key={o.value}
              value={o.value}
              onSelect={() => {
                onChange(o.value, o);
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 data-[selected=true]:bg-surface-hover"
            >
              <Check className={cn('size-4 text-accent', o.value === value ? 'opacity-100' : 'opacity-0')} />
              <span className="min-w-0 flex-1 truncate">{o.label}</span>
              {o.hint ? <span className="truncate text-xs text-subtle">{o.hint}</span> : null}
            </Command.Item>
          ))}
        </Command.List>
      </Command>
    </Popover>
  );
}
